import React, { Fragment, useEffect, useCallback } from 'react';
import { NextSeo, ProductJsonLd } from 'next-seo';
import Head from 'next/head';
import truncate from 'lodash/truncate';
import trim from 'lodash/trim';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { keywords } from 'next-seo.config';
import { HOST, isBrowser } from '@lib/helpers';
import { formatCurrencyVN as formatPrice } from '@lib/helpers';
import { productHref } from '@helpers/route';

import CanonicalDetector from '@helpers/canonical-detector';
import fromUnixTime from 'date-fns/fromUnixTime';
import formatDate from '@helpers/format-date';

const SEOComponent = ({ data }) => {
  const id = get(data, 'product.id');
  const sku = get(data, 'product.sku');
  const price = get(data, 'product.price', 0);
  const metaTitle = get(data, 'product.attributes.metaTitle');
  const metaDescription = get(data, 'product.attributes.metaDescription');
  const name = isEmpty(metaTitle) ? get(data, 'product.name') : metaTitle;
  const description = trim(metaDescription).replace(/(\r\n|\n|\r)/g, '');
  const url = `${HOST}${productHref(id, name)}`;
  const title = truncate(
    `${trim(name).replace(/(\r\n|\n|\r)/g, '')} | ${keywords}`,
    { length: 60 }
  );
  const images = get(data, 'product.attributes.galleries', []);

  let aggregateRating = null;
  let reviews = [];
  const ratingSummary = get(data, 'summary', {});
  const reviewRating = get(data, 'ratings', []);
  const googleIndex = get(data, 'product.attributes.googleIndex', true);
  const canViewRating = get(data, 'product.attributes.canViewRating', false);

  if (!isEmpty(ratingSummary) && !isEmpty(reviewRating) && canViewRating) {
    const ratingCount = get(ratingSummary, 'ratingCount', []);

    let starCount = 0;
    let starSum = 0;
    let ratingValue = null;
    if (Array.isArray(ratingCount)) {
      ratingCount.forEach((value, index) => {
        starCount += value;
        starSum += value * (index + 1);
      });

      if (starCount > 0) {
        ratingValue = (starSum / starCount).toFixed(2);
      }
    }

    if (ratingValue !== null) {
      aggregateRating = {
        ratingValue: ratingValue,
        ratingCount: get(ratingSummary, 'total'),
        reviewCount: get(ratingSummary, 'total'),
        bestRating: '5',
      };
    }

    if (Array.isArray(ratingCount)) {
      (reviewRating as Array<any>).forEach((value, index) => {
        reviews.push({
          author: {
            type: 'Person',
            name: get(value, 'author'),
          },
          datePublished: formatDate(
            fromUnixTime(get(value, 'time')),
            'yyyy-MM-dd'
          ),
          reviewBody: get(value, 'comment'),
          name: name,
          reviewRating: {
            bestRating: '5',
            ratingValue: `${get(value, 'ratingStar')}`,
            worstRating: '1',
          },
        });
      });
    }
  }

  useEffect(() => {
    if (dataLayer && id) {
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      dataLayer.push({
        ecommerce: {
          detail: {
            products: [
              {
                name: name, // Name or ID is required.
                id: id,
                price: price,
                brand: '',
                category: '',
                variant: '',
              },
            ],
          },
        },
      });
    }
  }, [name, id, price]);

  return (
    <>
      <Head>
        <meta property="og:image" content={images[0]} key="image" />
      </Head>
      <NextSeo
        title={title}
        titleTemplate="%s"
        description={description}
        openGraph={{
          title,
          url,
          description,
        }}
        canonical={url}
        noindex={!googleIndex}
      />
      <ProductJsonLd
        productName={name}
        images={images}
        sku={sku}
        aggregateRating={aggregateRating}
        description={description}
        offers={{
          price: formatPrice(price),
          priceCurrency: 'VND',
          itemCondition: 'http://schema.org/UsedCondition',
          availability: 'http://schema.org/InStock',
          url,
          seller: {
            name: 'Sơn Thuỷ',
          },
        }}
        reviews={reviews}
      />

      {!!url && isBrowser && <CanonicalDetector url={url} />}
    </>
  );
};

export default SEOComponent;
